import { TabsContent } from "../components/ui/tabs";
import { useState, useEffect } from "react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import type { FinancialAccount, FinancialAccountMapping } from "global-types";
import { useProformaLineItems, useSaveFinancialAccountMappings } from "../hooks/queryHooks";
import { Save, AlertCircle, CheckCircle } from "lucide-react";

interface FinancialAccountsProps {
  financialAccounts: FinancialAccount[];
  accountId?: string;
}

export const FinancialAccounts = ({ financialAccounts = [], accountId }: FinancialAccountsProps) => {
  const [accountMappings, setAccountMappings] = useState<Record<string, string>>({});
  const [_initialMappings, setInitialMappings] = useState<Record<string, string>>({});
  const [originalMappings, setOriginalMappings] = useState<Record<string, string>>({});
  const [bulkStartRange, setBulkStartRange] = useState('');
  const [bulkEndRange, setBulkEndRange] = useState('');
  const [bulkLineItem, setBulkLineItem] = useState('');
  const [dropdownStates, setDropdownStates] = useState<Record<string, boolean>>({});
  const [highlightedIndex, setHighlightedIndex] = useState<Record<string, number>>({});
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<'account_identifier' | 'account_name'>('account_identifier');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [showUnmappedOnly, setShowUnmappedOnly] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [saveResult, setSaveResult] = useState<{ count: number; message: string; newCount?: number; updatedCount?: number } | null>(null);

  // Fetch proforma line items from API
  const { data: proformaLineItemsData, isLoading: isLoadingLineItems, error: lineItemsError } = useProformaLineItems(accountId || '');
  
  // Save mappings mutation
  const saveMappingsMutation = useSaveFinancialAccountMappings(accountId);
  
  // Extract line item names from the API response
  const proformaLineItems = proformaLineItemsData?.proforma_line_items?.map(item => item.name) || [];

  // Initialize mappings from financial accounts data
  useEffect(() => {
    if (financialAccounts.length > 0) {
      const initialMappingsData: Record<string, string> = {};
      financialAccounts.forEach(account => {
        initialMappingsData[account.account_identifier] = account.proforma_line_item_name || '';
      });
      
      setOriginalMappings(initialMappingsData); // Store the original state
      setInitialMappings(initialMappingsData);
      setAccountMappings(initialMappingsData);
    }
  }, [financialAccounts]);

  // Calculate changed mappings - compare against original state
  const changedMappings = Object.keys(accountMappings).filter(
    accountId => accountMappings[accountId] !== originalMappings[accountId]
  );

  // Separate new mappings from updates
  const newMappings = changedMappings.filter(
    accountId => !originalMappings[accountId] && accountMappings[accountId]
  );
  const updatedMappings = changedMappings.filter(
    accountId => originalMappings[accountId] && accountMappings[accountId] !== originalMappings[accountId]
  );

  const hasChanges = changedMappings.length > 0;

  // Close dropdown when clicking outside and validate input
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('.dropdown-container')) {
        setDropdownStates({});
        
        // Validate and clear invalid entries
        const newMappings = { ...accountMappings };
        let hasChanges = false;
        
        Object.keys(newMappings).forEach(accountId => {
          const currentValue = newMappings[accountId];
          if (currentValue && !proformaLineItems.includes(currentValue)) {
            newMappings[accountId] = '';
            hasChanges = true;
          }
        });
        
        if (hasChanges) {
          setAccountMappings(newMappings);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [accountMappings, proformaLineItems]);

  const handleAccountMapping = (accountId: string, lineItem: string) => {
    setAccountMappings(prev => ({
      ...prev,
      [accountId]: lineItem
    }));
  };

  const handleSaveMappings = async () => {
    if (!accountId || !hasChanges) return;

    const mappings: FinancialAccountMapping[] = changedMappings.map(accountIdentifier => ({
      account_identifier: accountIdentifier,
      proforma_line_item_name: accountMappings[accountIdentifier] || null,
    }));

    console.log('Saving mappings:', {
      accountId,
      changedMappings,
      mappings,
      hasChanges
    });

    try {
      const result = await saveMappingsMutation.mutateAsync({ mappings });
      console.log('Save successful:', result);
      
      // Update both initial and original mappings to reflect the saved state
      setInitialMappings(accountMappings);
      setOriginalMappings(accountMappings);
      
      // Show success modal
      setSaveResult({
        count: result.updated_count,
        message: result.message,
        newCount: newMappings.length,
        updatedCount: updatedMappings.length
      });
      setShowSuccessModal(true);
    } catch (error) {
      console.error('Failed to save mappings:', error);
      alert(`Failed to save mappings: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleBulkAssign = () => {
    const start = parseInt(bulkStartRange);
    const end = parseInt(bulkEndRange);
    
    if (!start || !end || !bulkLineItem || start > end) {
      alert('Please fill in all fields correctly: start range, end range, and line item.');
      return;
    }

    const newMappings = { ...accountMappings };
    for (let accountNum = start; accountNum <= end; accountNum++) {
      const accountId = accountNum.toString();
      if (financialAccounts.find(acc => acc.account_identifier === accountId)) {
        newMappings[accountId] = bulkLineItem;
      }
    }
    
    setAccountMappings(newMappings);
    setBulkStartRange('');
    setBulkEndRange('');
    setBulkLineItem('');
  };

  const toggleDropdown = (accountId: string) => {
    setDropdownStates(prev => ({
      ...prev,
      [accountId]: !prev[accountId]
    }));
  };

  const filterLineItems = (searchTerm: string) => {
    return proformaLineItems.filter(item => 
      item.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const handleKeyDown = (accountId: string, e: React.KeyboardEvent<HTMLInputElement>) => {
    const currentValue = accountMappings[accountId] || '';
    const filteredItems = filterLineItems(currentValue);
    const currentHighlighted = highlightedIndex[accountId] || 0;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (filteredItems.length > 0) {
        const newIndex = currentHighlighted < filteredItems.length - 1 ? currentHighlighted + 1 : 0;
        setHighlightedIndex(prev => ({ ...prev, [accountId]: newIndex }));
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (filteredItems.length > 0) {
        const newIndex = currentHighlighted > 0 ? currentHighlighted - 1 : filteredItems.length - 1;
        setHighlightedIndex(prev => ({ ...prev, [accountId]: newIndex }));
      }
    } else if (e.key === 'Enter') {
      if (filteredItems.length > 0 && dropdownStates[accountId]) {
        e.preventDefault();
        const selectedItem = filteredItems[currentHighlighted];
        handleAccountMapping(accountId, selectedItem);
        toggleDropdown(accountId);
      }
    } else if (e.key === 'Tab') {
      if (filteredItems.length > 0 && dropdownStates[accountId]) {
        e.preventDefault();
        const selectedItem = filteredItems[currentHighlighted];
        handleAccountMapping(accountId, selectedItem);
        toggleDropdown(accountId);
        
        // Find next input and focus it
        const currentInput = e.target as HTMLInputElement;
        const allInputs = document.querySelectorAll('input[placeholder="Select Line Item"]');
        const currentIndex = Array.from(allInputs).indexOf(currentInput);
        const nextInput = allInputs[currentIndex + 1] as HTMLInputElement;
        if (nextInput) {
          nextInput.focus();
          nextInput.select();
        }
      }
    }
  };

  const handleInputChange = (accountId: string, value: string) => {
    handleAccountMapping(accountId, value);
    setHighlightedIndex(prev => ({ ...prev, [accountId]: 0 }));
  };

  // Filter and sort accounts
  const filteredAndSortedAccounts = financialAccounts
    .filter(account => {
      // Apply search filter
      const matchesSearch = 
        account.account_identifier.toLowerCase().includes(searchTerm.toLowerCase()) ||
        account.account_name.toLowerCase().includes(searchTerm.toLowerCase());
      
      // Apply unmapped filter - use original mappings for column assignment
      const isUnmapped = !originalMappings[account.account_identifier] || originalMappings[account.account_identifier] === '';
      const matchesUnmappedFilter = showUnmappedOnly ? isUnmapped : true;
      
      return matchesSearch && matchesUnmappedFilter;
    })
    .sort((a, b) => {
      const aValue = a[sortField];
      const bValue = b[sortField];
      
      if (sortDirection === 'asc') {
        return aValue.localeCompare(bValue);
      } else {
        return bValue.localeCompare(aValue);
      }
    });

  const handleSort = (field: 'account_identifier' | 'account_name') => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Show loading state
  if (isLoadingLineItems) {
    return (
      <TabsContent value="financial" className="mt-4">
        <div className="space-y-6">
          <div className="flex items-center gap-3 mb-6">
            <h2 className="text-xl font-semibold">General Ledger Account Mapping</h2>
          </div>
          <div className="flex items-center justify-center py-8">
            <div className="text-muted-foreground">Loading proforma line items...</div>
          </div>
        </div>
      </TabsContent>
    );
  }

  // Show error state
  if (lineItemsError) {
    return (
      <TabsContent value="financial" className="mt-4">
        <div className="space-y-6">
          <div className="flex items-center gap-3 mb-6">
            <h2 className="text-xl font-semibold">General Ledger Account Mapping</h2>
          </div>
          <div className="flex items-center justify-center py-8">
            <div className="text-destructive">
              Error loading proforma line items: {lineItemsError instanceof Error ? lineItemsError.message : 'Unknown error'}
            </div>
          </div>
        </div>
      </TabsContent>
    );
  }

  return (
    <TabsContent value="financial" className="mt-4">
      <div className="space-y-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold">General Ledger Account Mapping</h2>
          <div className="flex items-center gap-3">
            {hasChanges && (
              <div className="flex items-center gap-2 text-sm text-amber-600">
                <AlertCircle className="h-4 w-4" />
                <span>
                  {newMappings.length > 0 && `${newMappings.length} new`}
                  {newMappings.length > 0 && updatedMappings.length > 0 && ', '}
                  {updatedMappings.length > 0 && `${updatedMappings.length} updated`}
                  {changedMappings.length > 0 && ` (${changedMappings.length} total)`}
                </span>
              </div>
            )}
            <Button 
              variant="outline"
              onClick={() => {
                setAccountMappings(originalMappings);
              }}
            >
              Clear All
            </Button>
            {hasChanges && (
              <Button
                onClick={handleSaveMappings}
                disabled={saveMappingsMutation.isPending}
                className="flex items-center gap-2"
              >
                <Save className="h-4 w-4" />
                {saveMappingsMutation.isPending ? 'Saving...' : `Save ${changedMappings.length} Change${changedMappings.length !== 1 ? 's' : ''}`}
              </Button>
            )}
          </div>
        </div>

        {/* No proforma line items warning */}
        {proformaLineItems.length === 0 && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <div className="text-yellow-800">
              <strong>No proforma line items found.</strong> Please add proforma line items to this account before mapping financial accounts.
            </div>
          </div>
        )}

        {/* Bulk Assignment Section */}
        <div className="bg-muted/50 border border-border rounded-lg p-4 mb-6">
          <h3 className="text-lg font-medium mb-4 text-foreground">Bulk Assignment</h3>
          <div className="flex flex-wrap items-center gap-4">
            <div>
              <Label htmlFor="bulkStartRange">Account Range Start:</Label>
              <Input
                id="bulkStartRange"
                type="text"
                value={bulkStartRange}
                onChange={(e) => setBulkStartRange(e.target.value)}
                placeholder="e.g., 4010"
                className="w-32 mt-1"
              />
            </div>
            
            <div>
              <Label htmlFor="bulkEndRange">End:</Label>
              <Input
                id="bulkEndRange"
                type="text"
                value={bulkEndRange}
                onChange={(e) => setBulkEndRange(e.target.value)}
                placeholder="e.g., 4020"
                className="w-32 mt-1"
              />
            </div>
            
            <div>
              <Label htmlFor="bulkLineItem">Line Item:</Label>
              <select
                id="bulkLineItem"
                value={bulkLineItem}
                onChange={(e) => setBulkLineItem(e.target.value)}
                className="w-64 mt-1 h-9 w-full min-w-0 rounded-md border bg-transparent px-3 py-1 text-sm shadow-xs transition-[color,box-shadow] outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]"
              >
                <option value="">Select Line Item</option>
                {proformaLineItems.map(item => (
                  <option key={item} value={item}>{item}</option>
                ))}
              </select>
            </div>
            
            <Button
              onClick={handleBulkAssign}
              className="mt-6"
            >
              Assign to Range
            </Button>
          </div>
        </div>

        {/* Search and Filter Controls */}
        <div className="flex items-center gap-4 mb-4">
          <div className="flex-1">
            <Label htmlFor="searchAccounts">Search Accounts:</Label>
            <Input
              id="searchAccounts"
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by account ID or name..."
              className="mt-1"
            />
          </div>
          <div className="flex items-center gap-2">
            <Switch
              id="showUnmappedOnly"
              checked={showUnmappedOnly}
              onCheckedChange={setShowUnmappedOnly}
            />
            <Label htmlFor="showUnmappedOnly" className="text-sm">
              Show unmapped only
            </Label>
          </div>
          <div className="text-sm text-muted-foreground">
            Showing {filteredAndSortedAccounts.length} of {financialAccounts.length} accounts
          </div>
        </div>

        {/* Ledger Accounts List */}
        <div className="grid grid-cols-2 gap-6">
          {/* Mapped Accounts */}
          <div className="space-y-2">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-foreground">
                Mapped Accounts ({filteredAndSortedAccounts.filter(account => 
                  originalMappings[account.account_identifier] && originalMappings[account.account_identifier] !== ''
                ).length})
              </h3>
            </div>
            
            {/* Sortable Headers */}
            <div className="grid grid-cols-[280px_120px_1fr] gap-3 p-3 bg-muted/50 border border-border rounded-md">
              <div>
                <span className="text-sm font-medium text-muted-foreground">Line Item</span>
              </div>
              <div>
                <button
                  onClick={() => handleSort('account_identifier')}
                  className="flex items-center gap-1 text-sm font-medium text-foreground hover:text-primary transition-colors"
                >
                  Account ID
                  {sortField === 'account_identifier' && (
                    <span className="text-xs">
                      {sortDirection === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </button>
              </div>
              <div>
                <button
                  onClick={() => handleSort('account_name')}
                  className="flex items-center gap-1 text-sm font-medium text-foreground hover:text-primary transition-colors"
                >
                  Account Name
                  {sortField === 'account_name' && (
                    <span className="text-xs">
                      {sortDirection === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </button>
              </div>
            </div>

            {filteredAndSortedAccounts
              .filter(account => 
                originalMappings[account.account_identifier] && originalMappings[account.account_identifier] !== ''
              )
              .map((account) => {
                const isChanged = accountMappings[account.account_identifier] !== originalMappings[account.account_identifier];
                
                return (
                  <div 
                    key={account.id} 
                    className={`grid grid-cols-[280px_120px_1fr] gap-3 p-3 border border-border rounded-md items-center ${
                      isChanged ? 'bg-amber-50 border-amber-200' : 'bg-muted/30'
                    }`}
                  >
                    <div className="relative dropdown-container">
                      <Input
                        type="text"
                        value={accountMappings[account.account_identifier] || ''}
                        onChange={(e) => handleInputChange(account.account_identifier, e.target.value)}
                        onKeyDown={(e) => handleKeyDown(account.account_identifier, e)}
                        onFocus={() => toggleDropdown(account.account_identifier)}
                        placeholder="Select Line Item"
                        className={`w-full ${isChanged ? 'border-amber-300 bg-amber-25' : ''}`}
                      />
                      
                      {dropdownStates[account.account_identifier] && (
                        <div className="absolute top-full left-0 right-0 bg-popover border border-border rounded-md shadow-lg max-h-48 overflow-y-auto z-10">
                          {filterLineItems(accountMappings[account.account_identifier] || '').map((lineItem, index) => (
                            <div
                              key={lineItem}
                              className={`px-3 py-2 cursor-pointer ${
                                index === (highlightedIndex[account.account_identifier] || 0) 
                                  ? 'bg-accent text-accent-foreground' 
                                  : 'hover:bg-accent/50'
                              }`}
                              onClick={() => {
                                handleAccountMapping(account.account_identifier, lineItem);
                                toggleDropdown(account.account_identifier);
                              }}
                            >
                              {lineItem}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    
                    <div className="flex justify-center">
                      <span className="bg-primary text-primary-foreground px-2 py-1 rounded text-xs font-bold">
                        {account.account_identifier}
                      </span>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-foreground">{account.account_name}</span>
                      {isChanged && (
                        <div className="flex items-center gap-1 text-amber-600 text-xs">
                          <AlertCircle className="h-3 w-3" />
                          <span>Modified</span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
          </div>

          {/* Unmapped Accounts */}
          <div className="space-y-2">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-foreground">
                Unmapped Accounts ({filteredAndSortedAccounts.filter(account => 
                  !originalMappings[account.account_identifier] || originalMappings[account.account_identifier] === ''
                ).length})
              </h3>
            </div>
            
            {/* Sortable Headers */}
            <div className="grid grid-cols-[280px_120px_1fr] gap-3 p-3 bg-muted/50 border border-border rounded-md">
              <div>
                <span className="text-sm font-medium text-muted-foreground">Line Item</span>
              </div>
              <div>
                <button
                  onClick={() => handleSort('account_identifier')}
                  className="flex items-center gap-1 text-sm font-medium text-foreground hover:text-primary transition-colors"
                >
                  Account ID
                  {sortField === 'account_identifier' && (
                    <span className="text-xs">
                      {sortDirection === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </button>
              </div>
              <div>
                <button
                  onClick={() => handleSort('account_name')}
                  className="flex items-center gap-1 text-sm font-medium text-foreground hover:text-primary transition-colors"
                >
                  Account Name
                  {sortField === 'account_name' && (
                    <span className="text-xs">
                      {sortDirection === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </button>
              </div>
            </div>

            {filteredAndSortedAccounts
              .filter(account => 
                !originalMappings[account.account_identifier] || originalMappings[account.account_identifier] === ''
              )
              .map((account) => {
                const isChanged = accountMappings[account.account_identifier] !== originalMappings[account.account_identifier];
                
                return (
                  <div 
                    key={account.id} 
                    className={`grid grid-cols-[280px_120px_1fr] gap-3 p-3 border border-border rounded-md items-center ${
                      isChanged ? 'bg-amber-50 border-amber-200' : 'bg-muted/30'
                    }`}
                  >
                    <div className="relative dropdown-container">
                      <Input
                        type="text"
                        value={accountMappings[account.account_identifier] || ''}
                        onChange={(e) => handleInputChange(account.account_identifier, e.target.value)}
                        onKeyDown={(e) => handleKeyDown(account.account_identifier, e)}
                        onFocus={() => toggleDropdown(account.account_identifier)}
                        placeholder="Select Line Item"
                        className={`w-full ${isChanged ? 'border-amber-300 bg-amber-25' : ''}`}
                      />
                      
                      {dropdownStates[account.account_identifier] && (
                        <div className="absolute top-full left-0 right-0 bg-popover border border-border rounded-md shadow-lg max-h-48 overflow-y-auto z-10">
                          {filterLineItems(accountMappings[account.account_identifier] || '').map((lineItem, index) => (
                            <div
                              key={lineItem}
                              className={`px-3 py-2 cursor-pointer ${
                                index === (highlightedIndex[account.account_identifier] || 0) 
                                  ? 'bg-accent text-accent-foreground' 
                                  : 'hover:bg-accent/50'
                              }`}
                              onClick={() => {
                                handleAccountMapping(account.account_identifier, lineItem);
                                toggleDropdown(account.account_identifier);
                              }}
                            >
                              {lineItem}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    
                    <div className="flex justify-center">
                      <span className="bg-primary text-primary-foreground px-2 py-1 rounded text-xs font-bold">
                        {account.account_identifier}
                      </span>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-foreground">{account.account_name}</span>
                      {isChanged && (
                        <div className="flex items-center gap-1 text-amber-600 text-xs">
                          <AlertCircle className="h-3 w-3" />
                          <span>Modified</span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      </div>

      {/* Success Modal */}
      <Dialog open={showSuccessModal} onOpenChange={setShowSuccessModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-600" />
              Save Successful
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              {saveResult?.message || `Successfully saved ${saveResult?.count || 0} mapping(s).`}
            </p>
            {saveResult && (
              <div className="text-xs text-muted-foreground space-y-1">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <span>{saveResult.newCount ?? 0} new mapping{(saveResult.newCount ?? 0) !== 1 ? 's' : ''}</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                  <span>{saveResult.updatedCount ?? 0} updated mapping{(saveResult.updatedCount ?? 0) !== 1 ? 's' : ''}</span>
                </div>
              </div>
            )}
            <div className="flex justify-end">
              <Button onClick={() => setShowSuccessModal(false)}>
                Close
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </TabsContent>
  );
}; 